import { useEffect, useRef, useState } from 'react';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { useSession } from '@/platform/session';

declare global {
  interface Window {
    adsbygoogle?: unknown[];
  }
}

let scriptLoaded = false;
function loadAdSense(client: string) {
  if (scriptLoaded) return;
  scriptLoaded = true;
  const s = document.createElement('script');
  s.async = true;
  s.crossOrigin = 'anonymous';
  s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${encodeURIComponent(client)}`;
  document.head.appendChild(s);
}

/**
 * Room for a vertical ad to the right of the lg (1200px) content column, 32px away from it and
 * from the window edge, at least 160px wide and 600px tall below the 64px app bar.
 */
const WIDE = '(min-width:1680px) and (min-height:760px)';

const sideSx = {
  position: 'fixed',
  top: 88,
  left: 'calc(50% + 600px + 32px)',
  width: 'min(300px, calc(50% - 600px - 64px))',
  textAlign: 'center',
} as const;

/**
 * One AdSense display unit, for people on free use (courtesy, trial): /me sends the publisher
 * and the unit only to them (access.shows_ads in the API), so this renders nothing otherwise.
 * Lives in AppLayout, never on the exam screen (a fullscreen route). Below the page content; on
 * wide screens a fixed vertical ad beside it instead, so a page never shows both.
 */
export function AdSlot() {
  const { me } = useSession();
  // noSsr: know the real width on the first render, so we never request an ad we then throw away.
  const wide = useMediaQuery(WIDE, { noSsr: true });

  if (!me.ads) return null;
  return (
    <AdUnit key={wide ? 'side' : 'inline'} client={me.ads.client} slot={me.ads.slot} side={wide} />
  );
}

/** The "Publicidade" label shows only once an ad is filled; an unfilled slot disappears entirely. */
function AdUnit({ client, slot, side }: { client: string; slot: string; side: boolean }) {
  const pushed = useRef(false);
  const insRef = useRef<HTMLModElement>(null);
  // AdSense sets data-ad-status="filled" | "unfilled" on the <ins> once it decides.
  const [status, setStatus] = useState<string | null>(null);

  useEffect(() => {
    const ins = insRef.current;
    if (!ins) return;
    const observer = new MutationObserver(() => setStatus(ins.getAttribute('data-ad-status')));
    observer.observe(ins, { attributes: true, attributeFilter: ['data-ad-status'] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (pushed.current) return;
    pushed.current = true;
    loadAdSense(client);
    try {
      (window.adsbygoogle = window.adsbygoogle ?? []).push({});
    } catch {
      // Ad blockers and AdSense hiccups must never break the page.
    }
  }, [client]);

  if (status === 'unfilled') return null;
  return (
    <Box
      component="aside"
      aria-label="Publicidade"
      sx={side ? sideSx : { mt: 6, textAlign: 'center' }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        component="div"
        sx={{ mb: 0.5, visibility: status === 'filled' ? 'visible' : 'hidden' }}
      >
        Publicidade
      </Typography>
      <ins
        ref={insRef}
        className="adsbygoogle"
        style={{ display: 'block', minHeight: side ? 250 : 90 }}
        data-ad-client={client}
        data-ad-slot={slot}
        data-ad-format={side ? 'vertical' : 'auto'}
        data-full-width-responsive={side ? 'false' : 'true'}
      />
    </Box>
  );
}
